import {
  View,
  StyleSheet,
  Text,
  Platform,
  Modal,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import React, { useMemo, useState } from "react";
import ChartStyles from "./chartStyles";
import { Divider, IconButton, Tooltip } from "react-native-paper";
import {
  VictoryBar,
  VictoryChart,
  VictoryGroup,
  VictoryAxis,
  VictoryLabel,
} from "victory-native";

import ChartExportDialog from "./ChartExportDialog";
import { runExport } from "./ChartExport";

// ── Types ─────────────────────────────────────────────────────────────────────

interface ChartDataset {
  data?: (number | null | undefined)[];
  label?: string;
  name?: string;
  color?: (opacity: number) => string;
}

interface ChartDataProps {
  labels?: string[];
  datasets?: ChartDataset[];
}

interface LegendItem {
  name: string;
}

interface BarChartScreenProps {
  label?: string[];
  data?: ChartDataProps;
  ChartTitle: string;
  subTitle?: string;
  legend?: LegendItem[];
}

// ── Fullscreen modal styles — mirrors piChart-builder pattern ─────────────────

const fsStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.55)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  card: {
    backgroundColor: "#F9FCFF",
    borderRadius: 16,
    width: "100%",
    maxWidth: 920,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  headerIcon: {
    backgroundColor: "#102B27",
    borderRadius: 8,
    height: 44,
    width: 44,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  headerTitles: {
    flex: 1,
  },
  headerTitle: {
    color: "#102B27",
    fontWeight: "bold",
    fontSize: 16,
  },
  headerSubTitle: {
    color: "#102B27",
    fontWeight: "bold",
    fontSize: 12,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  chartArea: {
    padding: 16,
    minHeight: 480,
    justifyContent: "center",
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildBarSupportingRows(p: {
  data?: ChartDataProps;
  legend?: LegendItem[];
}) {
  const labels = p?.data?.labels ?? [];
  const datasets = p?.data?.datasets ?? [];
  const legend = p?.legend ?? [];

  if (datasets.length <= 1) {
    const ds = datasets[0] ?? { data: [] };
    return (ds.data ?? []).map((v, idx) => ({
      Label: String(labels[idx] ?? idx),
      Value: Number(v ?? 0),
    }));
  }

  return datasets.flatMap((ds, i) => {
    const seriesName =
      legend[i]?.name ?? ds?.label ?? ds?.name ?? `Series ${i + 1}`;
    return (ds.data ?? []).map((v, idx) => ({
      Label: String(labels[idx] ?? idx),
      Series: String(seriesName),
      Value: Number(v ?? 0),
    }));
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

function GroupBarChartsComp(props: BarChartScreenProps) {
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [fsChartWidth, setFsChartWidth] = useState<number>(0);
  const [showFull, setShowFull] = useState<boolean>(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [fsDownloadOpen, setFsDownloadOpen] = useState(false);

  const onChartLayout = (event: { nativeEvent: { layout: { width: number } } }) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const baseId = useMemo(
    () =>
      (props.ChartTitle || "chart")
        .replace(/[\\/:*?"<>| ]+/g, "-")
        .toLowerCase(),
    [props.ChartTitle]
  );

  const exportRootId = `${baseId}-bar-export-root`;
  const fsExportRootId = `${baseId}-bar-fs-export-root`;

  // Last-5 view (most recent data points)
  const transformedDatasets = useMemo(() => {
    const firstDataLen = props.data?.datasets?.[0]?.data?.length ?? 0;
    const startIdx = Math.max(0, firstDataLen - 5);
    return (props.data?.datasets ?? []).map((dataset) => ({
      data: (dataset.data ?? []).slice(startIdx).map((value, i) => ({
        x: props.data?.labels?.[startIdx + i] ?? String(startIdx + i),
        y: Number(value) || 0,
      })),
    }));
  }, [props.data]);

  // All-data view (fullscreen + export)
  const transformedDatasetsAll = useMemo(
    () =>
      (props.data?.datasets ?? []).map((dataset) => ({
        data: (dataset.data ?? []).map((value, i) => ({
          x: props.data?.labels?.[i] ?? String(i),
          y: Number(value) || 0,
        })),
      })),
    [props.data]
  );

  const colors = useMemo(
    () =>
      (props.data?.datasets ?? []).map(
        (ds) => ds?.color?.(1) ?? "#4BB75F"
      ),
    [props.data]
  );

  const exportRowsWithLegend = useMemo(
    () => buildBarSupportingRows({ legend: props.legend, data: props.data }),
    [props.data, props.legend]
  );

  // Disable animation for large datasets to avoid UI-thread blocking on mobile
  const animationEnabled = useMemo(() => {
    const total = (props.data?.datasets ?? []).reduce(
      (sum, ds) => sum + (ds.data?.length ?? 0),
      0
    );
    return total <= 100;
  }, [props.data]);

  const hasData = (props.data?.datasets?.length ?? 0) > 0;

  return (
    <View>
      <View style={[ChartStyles.style]}>
        {!hasData ? (
          <View>
            <Text style={ChartStyles.chartTitle}>
              {"There is no data available to show " + props.ChartTitle}
            </Text>
          </View>
        ) : (
          <View>
            <View
              style={[ChartStyles.container, ChartStyles.box2]}
              onLayout={onChartLayout}
              {...(Platform.OS === "web"
                ? ({ id: exportRootId } as any)
                : {})}
            >
              {/* Header */}
              <View
                style={{
                  alignItems: "center",
                  flexDirection: "row",
                  width: containerWidth ?? undefined,
                  justifyContent: "center",
                }}
              >
                <TouchableOpacity style={ChartStyles.TitleIconHolder}>
                  <IconButton
                    icon="chart-box-outline"
                    iconColor={"#ffffff"}
                    size={30}
                  />
                </TouchableOpacity>

                <View style={ChartStyles.chartTitleContainer}>
                  <Text style={ChartStyles.chartTitle}>{props.ChartTitle}</Text>
                  <Text style={ChartStyles.chartSubTitle}>{props.subTitle}</Text>
                </View>

                {Platform.OS === "web" && (
                  <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <View style={ChartStyles.fullScreenIconContainer}>
                      <Tooltip title="Download">
                        <IconButton
                          icon="download"
                          iconColor={ChartStyles.chartTitle.color}
                          size={20}
                          onPress={() => setDownloadOpen(true)}
                        />
                      </Tooltip>
                    </View>
                    <View style={ChartStyles.fullScreenIconContainer}>
                      <Tooltip title="View in Full Screen">
                        <IconButton
                          icon="fullscreen"
                          iconColor={ChartStyles.chartTitle.color}
                          size={20}
                          onPress={() => setShowFull(true)}
                        />
                      </Tooltip>
                    </View>
                  </View>
                )}
              </View>

              {/* Visible chart (top 5) */}
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ height: 360 }}
              >
                <VictoryChart
                  width={containerWidth ?? 350}
                  domainPadding={{ x: 20, y: 20 }}
                  padding={{ top: 36, bottom: 70, left: 60, right: 24 }}
                >
                  <VictoryAxis
                    tickLabelComponent={<VictoryLabel angle={45} />}
                    style={{ tickLabels: { fontSize: 10, padding: 12 } }}
                  />
                  <VictoryAxis
                    dependentAxis
                    style={{ tickLabels: { fontSize: 10, padding: 8 } }}
                  />
                  <VictoryGroup style={{ data: { fillOpacity: 0.9 } }}>
                    {transformedDatasets.map((dataset, index) => (
                      <VictoryBar
                        key={index}
                        data={dataset.data}
                        barRatio={0.25}
                        labels={({ datum }) => datum.y ? String(datum.y) : ""}
                        labelComponent={
                          <VictoryLabel
                            dy={-6}
                            style={{ fontSize: 9, fill: "#333" }}
                          />
                        }
                        animate={
                          animationEnabled
                            ? { duration: 1500, onLoad: { duration: 1500 } }
                            : false
                        }
                        style={{
                          data: { stroke: colors[index], fill: colors[index] },
                        }}
                      />
                    ))}
                  </VictoryGroup>
                </VictoryChart>
              </ScrollView>

              {/* Export dialog (web only) */}
              {Platform.OS === "web" && (
                <ChartExportDialog
                  open={downloadOpen}
                  title={`Download: ${props.ChartTitle}`}
                  hasSupportingData={transformedDatasetsAll.length > 0}
                  onClose={() => setDownloadOpen(false)}
                  onConfirm={async (sel) => {
                    setDownloadOpen(false);
                    const safeBase = (props.ChartTitle || "Chart")
                      .replace(/[\\/:*?"<>|]+/g, "-")
                      .trim();
                    await runExport(sel, {
                      baseName: safeBase,
                      exportSvgId: exportRootId,
                      exportRows: exportRowsWithLegend,
                    });
                  }}
                  exportRows={[]}
                />
              )}
            </View>
          </View>
        )}
      </View>

      {/* Fullscreen modal (all data) — card pattern matching piChart-builder */}
      {showFull && (
        <Modal
          animationType="fade"
          transparent
          visible={showFull}
          onRequestClose={() => setShowFull(false)}
        >
          <View style={fsStyles.overlay}>
            <View style={fsStyles.card}>
              {/* Header */}
              <View style={fsStyles.header}>
                <View style={fsStyles.headerIcon}>
                  <IconButton icon="chart-box-outline" iconColor="#ffffff" size={22} />
                </View>
                <View style={fsStyles.headerTitles}>
                  <Text style={fsStyles.headerTitle}>{props.ChartTitle}</Text>
                  {props.subTitle ? (
                    <Text style={fsStyles.headerSubTitle}>{props.subTitle}</Text>
                  ) : null}
                </View>
                <View style={fsStyles.headerActions}>
                  {Platform.OS === "web" && (
                    <Tooltip title="Download">
                      <IconButton
                        icon="download"
                        iconColor={ChartStyles.chartTitle.color}
                        size={20}
                        onPress={() => setFsDownloadOpen(true)}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="Close">
                    <IconButton
                      icon="close"
                      iconColor={ChartStyles.chartTitle.color}
                      size={24}
                      onPress={() => setShowFull(false)}
                    />
                  </Tooltip>
                </View>
              </View>
              <Divider />

              {/* Chart area — measures its own width before rendering */}
              <View
                style={fsStyles.chartArea}
                onLayout={(e) => setFsChartWidth(e.nativeEvent.layout.width - 32)}
              >
                {fsChartWidth > 0 && (
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <View
                      {...(Platform.OS === "web"
                        ? ({ id: fsExportRootId } as any)
                        : {})}
                    >
                    <VictoryChart
                      width={fsChartWidth}
                      domainPadding={{ x: 50, y: 20 }}
                      padding={{ top: 36, bottom: 90, left: 70, right: 24 }}
                    >
                      <VictoryAxis
                        tickLabelComponent={<VictoryLabel angle={45} />}
                        style={{ tickLabels: { fontSize: 10, padding: 12 } }}
                      />
                      <VictoryAxis
                        dependentAxis
                        style={{ tickLabels: { fontSize: 10, padding: 8 } }}
                      />
                      <VictoryGroup style={{ data: { fillOpacity: 0.9 } }}>
                        {transformedDatasetsAll.map((dataset, index) => (
                          <VictoryBar
                            key={index}
                            data={dataset.data}
                            barWidth={14}
                            labels={({ datum }) => datum.y ? String(datum.y) : ""}
                            labelComponent={
                              <VictoryLabel
                                dy={-6}
                                style={{ fontSize: 9, fill: "#333" }}
                              />
                            }
                            animate={
                              animationEnabled
                                ? { duration: 2000, onLoad: { duration: 2000 } }
                                : false
                            }
                            style={{
                              data: { stroke: colors[index], fill: colors[index] },
                            }}
                          />
                        ))}
                      </VictoryGroup>
                    </VictoryChart>
                    </View>
                  </ScrollView>
                )}
              </View>

              {/* Fullscreen export dialog — rendered inside Modal so z-index 99999 beats the overlay */}
              {Platform.OS === "web" && (
                <ChartExportDialog
                  open={fsDownloadOpen}
                  title={`Download: ${props.ChartTitle}`}
                  hasSupportingData={transformedDatasetsAll.length > 0}
                  onClose={() => setFsDownloadOpen(false)}
                  onConfirm={async (sel) => {
                    setFsDownloadOpen(false);
                    const safeBase = (props.ChartTitle || "Chart")
                      .replace(/[\\/:*?"<>|]+/g, "-")
                      .trim();
                    await runExport(sel, {
                      baseName: safeBase,
                      exportSvgId: fsExportRootId,
                      exportRows: exportRowsWithLegend,
                    });
                  }}
                  exportRows={[]}
                />
              )}
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
}

export default GroupBarChartsComp;
